import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNetworthSnapshotDetail } from "../../hooks/networth";
import { formatDateShort } from "../../lib/format";
import type { AssetClass, SnapshotListItem } from "../../lib/networth";

interface Props {
  snapshots: SnapshotListItem[];
  onOpenDrill: (stichtag: string) => void;
}

const CLASSES: AssetClass[] = ["LIQUIDE", "ANLAGEN", "IMMOBILIE", "SONSTIGE", "VERBINDLICHKEITEN"];

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

export function CardListStichtage({ snapshots, onOpenDrill }: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="space-y-2">
      {snapshots.map((s) => (
        <StichtagCard
          key={s.stichtag}
          snapshot={s}
          expanded={expanded === s.stichtag}
          onToggle={() => setExpanded(expanded === s.stichtag ? null : s.stichtag)}
          onOpenDrill={() => onOpenDrill(s.stichtag)}
        />
      ))}
    </div>
  );
}

interface CardProps {
  snapshot: SnapshotListItem;
  expanded: boolean;
  onToggle: () => void;
  onOpenDrill: () => void;
}

function StichtagCard({ snapshot, expanded, onToggle, onOpenDrill }: CardProps) {
  const { t } = useTranslation();
  const detail = useNetworthSnapshotDetail(expanded ? snapshot.stichtag : "");

  return (
    <div className="fv-card p-4">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between min-h-[44px]"
        aria-expanded={expanded}
      >
        <span className="font-medium">{formatDateShort(snapshot.stichtag)}</span>
        <div className="flex items-center gap-2">
          <span className="num font-semibold">{formatEur(snapshot.totalCents)}</span>
          <svg
            className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      </button>
      {expanded && detail.data && (
        <div className="mt-3 pt-3 space-y-1.5 border-t" style={{ borderColor: "var(--border)" }}>
          {CLASSES.map((c) => {
            const cls = detail.data.classes[c];
            const value = cls?.valueCents ?? 0;
            return (
              <div key={c} className="flex justify-between text-sm">
                <span style={{ color: "var(--muted)" }}>
                  {t(`networth.assetClass.${c}`, { defaultValue: c })}
                </span>
                <span className="num">{formatEur(value)}</span>
              </div>
            );
          })}
          <button
            type="button"
            onClick={onOpenDrill}
            className="w-full mt-3 px-3 py-2 text-sm font-medium rounded-md border min-h-[44px]"
            style={{ borderColor: "var(--border)", color: "var(--accent)" }}
          >
            {t("networth.detail.open_drill", { defaultValue: "Drill-Modal öffnen" })}
          </button>
        </div>
      )}
    </div>
  );
}
